import type { NotificationDefImportItem } from "./types";

const SUPPORTED_VERSION = 1;

export class NotificationDefImportParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotificationDefImportParseError";
  }
}

function assertImportVersion(version: unknown): void {
  if (version === undefined) {
    return;
  }
  if (version !== SUPPORTED_VERSION) {
    throw new NotificationDefImportParseError(
      `Unsupported version: ${String(version)}. Expected ${SUPPORTED_VERSION}.`
    );
  }
}

function asItemArray(value: unknown): NotificationDefImportItem[] {
  if (!Array.isArray(value)) {
    throw new NotificationDefImportParseError("Expected an array of items.");
  }
  if (value.length === 0) {
    throw new NotificationDefImportParseError("No items to import.");
  }
  return value.map((item, index) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      throw new NotificationDefImportParseError(
        `Item at index ${index} must be an object.`
      );
    }
    return item as NotificationDefImportItem;
  });
}

export function parseNotificationDefImportJson(
  parsed: unknown
): NotificationDefImportItem[] {
  if (Array.isArray(parsed)) {
    return asItemArray(parsed);
  }

  if (!parsed || typeof parsed !== "object") {
    throw new NotificationDefImportParseError(
      "JSON must be an object or array."
    );
  }

  const record = parsed as Record<string, unknown>;
  assertImportVersion(record.version);

  if (Array.isArray(record.items)) {
    return asItemArray(record.items);
  }

  if ("items" in record) {
    throw new NotificationDefImportParseError(
      '"items" must be a non-empty array.'
    );
  }

  return [record as NotificationDefImportItem];
}

export function parseNotificationDefImportFile(
  text: string
): NotificationDefImportItem[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new NotificationDefImportParseError("Invalid JSON.");
  }
  return parseNotificationDefImportJson(parsed);
}
